/**
 * FilePreviewModal Component
 * Full-screen modal for previewing files: PDF, DOCX, XLSX, TXT, MD, images
 * Fetches file content from the backend preview endpoint and renders inline.
 */

import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLanguage } from '@/hooks/useLanguage';
import api from '@/services/api';
import { message as antdMessage } from 'antd';
import { FilePreviewContent } from './FilePreviewContent';
import './FilePreviewModal.css';

interface FilePreviewModalProps {
  visible: boolean;
  filePath?: string;       // e.g. "tenants/{tenant_id}/uuid_filename.pdf" — for server files
  fileId?: string;         // file_id for DB lookup (works for all storage types)
  localFile?: File;        // For previewing local (not yet uploaded) files
  fileName: string;
  fileType?: string;       // MIME type
  /** Where to fetch the bytes, when a surface has its own door for them (the
   *  tenant tab's organization-context files). Wins over fileId / filePath —
   *  for the download button AND the preview body, which would otherwise ask
   *  the general route (/files/preview-by-id) and be refused a file that only
   *  the surface's own door opens. */
  downloadUrl?: string;
  onClose: () => void;
}

/** SVG Icons */
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export function FilePreviewModal({ visible, filePath, fileId, localFile, fileName, fileType, downloadUrl: ownUrl, onClose }: FilePreviewModalProps) {
  const { t } = useLanguage();

  /** Build the download URL: prefer fileId (works for all storage), fall back to filePath */
  const downloadUrl = ownUrl
    ?? (fileId ? `/files/download-by-id/${fileId}` : `/files/${filePath}`);

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [visible, onClose]);

  const handleDownload = async () => {
    // Previously this was a silent operation: any failure went to
    // console.error only, so a 403 / 500 / CORS block surfaced as
    // "the download button does nothing" with zero user feedback.
    // Now we surface failures via an antd toast AND log details for
    // diagnostics; on success we open the blob in a new tab as a
    // fallback if the synthetic <a> click is silently swallowed.
    let blob: Blob | null = null;
    try {
      if (localFile) {
        blob = localFile;
      } else {
        const response = await api.get(downloadUrl, { responseType: 'blob' });
        blob = response.data as Blob;
      }
      if (!blob || blob.size === 0) {
        throw new Error('Empty file received from server');
      }
    } catch (err: any) {
      // axios with responseType:'blob' wraps a JSON error body as a
      // Blob too — try to read it back to text so the toast shows
      // the real backend reason instead of just "Network Error".
      let detail = err?.message || String(err);
      try {
        const data = err?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          const parsed = JSON.parse(text);
          detail = parsed?.error || text || detail;
        }
      } catch { /* ignore parse failures */ }
      console.error('[file-download] failed:', err, detail);
      antdMessage.error(t('filePreview.downloadFailed') || `Download failed: ${detail}`);
      return;
    }

    // Trigger the browser save dialog. Append the <a> to the DOM
    // before clicking — Firefox requires it; revoke the blob URL
    // on a microtask delay so Safari has time to consume it
    // before the URL is invalidated.
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
  };

  if (!visible) return null;

  return ReactDOM.createPortal(
    <div className="fpm-overlay" onClick={onClose}>
      <div className="fpm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="fpm-header">
          <div className="fpm-header-info">
            <span dir="auto" className="fpm-file-name" title={fileName}>{fileName}</span>
          </div>
          <div className="fpm-header-actions">
            <button className="fpm-action-btn" onClick={handleDownload} title={t('filePreview.download')}>
              <DownloadIcon />
            </button>
            <button className="fpm-action-btn fpm-close-btn" onClick={onClose} title={t('common.close')}>
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="fpm-body">
          <FilePreviewContent
            filePath={filePath}
            fileId={fileId}
            localFile={localFile}
            fileName={fileName}
            fileType={fileType}
            previewUrl={ownUrl}
            active={visible}
            unsupportedAction={(
              <button className="fpm-download-cta" onClick={handleDownload}>
                <DownloadIcon />
                <span>{t('filePreview.downloadInstead')}</span>
              </button>
            )}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
